/**
 * Open/close plumbing for the ⌘K palette, and the topbar affordance that makes
 * the shortcut discoverable.
 *
 * The provider owns exactly ONE document keydown listener. A per-surface
 * binding is how a palette ends up opening twice on some screens and not at all
 * on others, and it is the reason the shortcut has to be registered above every
 * route rather than inside the shell.
 *
 * It must be mounted INSIDE `<BrowserRouter>`: the palette navigates, so it
 * needs a router context. It renders `<GlobalSearch/>` as a sibling of
 * `children`, above every route, so the palette survives the navigation it
 * causes rather than unmounting itself mid-transition.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Icon, Kbd } from '@w7/ui'
import { GlobalSearch } from './GlobalSearch'
import { isTextEntry, shortcutHint } from './search'
import './search.css'

interface GlobalSearchApi {
  open: boolean
  setOpen: (next: boolean) => void
}

const Ctx = createContext<GlobalSearchApi | null>(null)

/**
 * `{ open, setOpen }` for anything that wants to raise the palette — the topbar
 * trigger, and any empty state that says "press ⌘K". Shared state, so a screen
 * that offers the shortcut cannot open a second palette of its own.
 */
export function useGlobalSearch(): GlobalSearchApi {
  const api = useContext(Ctx)
  if (!api) {
    throw new Error('useGlobalSearch must be used inside <GlobalSearchProvider>')
  }
  return api
}

export interface GlobalSearchProviderProps {
  children: ReactNode
  /** Optional subtree to search instead of the whole downline. */
  scopeRootId?: string
}

export function GlobalSearchProvider({ children, scopeRootId }: GlobalSearchProviderProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // ⌘K / Ctrl-K fires wherever focus is. It is not a text-editing key, and
      // an operator halfway through typing a transfer amount still wants it.
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((current) => !current)
        return
      }
      // "/" is a character, so it stands down inside any field. Without the
      // guard, typing a fancy line like "6/8" into a filter opens the palette.
      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        if (isTextEntry(event.target)) return
        event.preventDefault()
        setOpen(true)
      }
    }

    // Capture, so a surface that stops propagation on its own keydown handler
    // cannot make the shortcut dead on that one screen.
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [])

  const close = useCallback(() => setOpen(false), [])
  const api = useMemo<GlobalSearchApi>(() => ({ open, setOpen }), [open])

  return (
    <Ctx.Provider value={api}>
      {children}
      <GlobalSearch open={open} onClose={close} {...(scopeRootId ? { scopeRootId } : {})} />
    </Ctx.Provider>
  )
}

/**
 * The topbar affordance.
 *
 * A shortcut nobody can see is a shortcut nobody uses, so ⌘K also gets a
 * field-shaped button. It is a real `<button>`, not an `<input>` — an input here
 * would take focus, take a caret, and then not be the box the operator is
 * actually typing into once the palette opens.
 *
 * Below 900px it collapses to the magnifier alone (see `search.css`). That is
 * done in CSS rather than by swapping in an `IconButton` so there is exactly one
 * button, with one accessible name, in the accessibility tree at every width —
 * rendering both and hiding one announces "Search" twice.
 */
export function SearchTrigger({ className }: { className?: string }) {
  const { setOpen } = useGlobalSearch()
  const hint = shortcutHint()

  return (
    <button
      type="button"
      className={`w7-cmdk-trigger w7-search-trigger${className ? ` ${className}` : ''}`}
      onClick={() => setOpen(true)}
      aria-label="Search agents, members, markets and transfers"
      aria-keyshortcuts="Meta+K Control+K"
    >
      <Icon name="search" size={15} />
      <span className="w7-search-trigger-text w7-truncate">Search agents, members, markets…</span>
      <Kbd>{hint}</Kbd>
    </button>
  )
}
